import React, { useState, useEffect, useMemo, useRef } from "react";
import { devLog } from "../../utils/devLog";
import styled from "styled-components";
import visa from '../../assets/payment-logos/visa.svg';
import masterCard from '../../assets/payment-logos/mastercard.svg';
import amex from '../../assets/payment-logos/amex.svg';
import discover from '../../assets/payment-logos/discover.svg';
import jcb from '../../assets/payment-logos/jcb.svg';
import applepayLogo from '../../assets/payment-logos/applepay.svg';
// import upiLogo from '../../assets/payment-logos/upi.svg';
import dLocal from '../../assets/payment-logos/dlocal.svg'
import pagbrasil from '../../assets/payment-logos/pagbrasil.svg'
import PaysafePaymentForm from "../integration/paysafe/PaysafePaymentForm";
import DlocalUpiForm from "../integration/dlocal/DlocalUpiForm";
import { useCheckout } from "../../context/CheckoutContext";
import DlocalPixForm from "../integration/dlocal/DlocalPixForm";
import PagbrasilPaymentForm from "../integration/pagbrasil/PagbrasilPaymentForm";
import PagbrasilCardForm from "../integration/pagbrasil/PagbrasilCardForm";
import DlocalCardForm from "../integration/dlocal/DlocalCardForm"
import AdyenCardForm from "../integration/adyen/AdyenCardForm";
import { useTranslation } from "../../hooks/useTranslation";
import {
  fetchCheckoutDetails,
  fetchGatewayConnection,
  invalidateIdempotentGet,
} from "../../api/api";
import CheckoutComCardForm from "../integration/checkout/CheckoutComCardForm";
import CheckoutComApplePayForm from "../integration/checkout/CheckoutComApplePayForm";
import CheckoutComKakaopayForm from "../integration/checkout/CheckoutComKakaopayForm";
import TrustlyOpenBankingForm from "../integration/trustly/TrustlyOpenBankingForm";
import NuveiCardForm from "../integration/nuvei/NuveiCardForm";
import PaymentMethodsSkeleton from "./PaymentMethodsSkeleton";
import BasisTheoryCardForm from "../integration/basistheory/BasisTheoryCardForm";
import {
  ALTRUON_VAULT_CARD_FORM_KEY,
  dedupeRoutingOptions,
  filterRoutingEntriesByCurrency,
  resolvePaymentForm,
} from "../../utils/routingEntries";

const SectionTitle = styled.h2`
  font-size: ${(props) => (props.$compact ? "15px" : "18px")};
  margin-bottom: ${(props) => (props.$compact ? "10px" : "15px")};
  font-weight: 600;
`;

const PaymentMethodsContainer = styled.div`
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? "10px" : "18px")};
`;

const PaymentOption = styled.div`
  border: none;
  border-radius: ${(props) => (props.$compact ? "8px" : "14px")};
  box-shadow: ${(props) =>
    props.$compact
      ? "0 1px 6px rgba(60, 90, 130, 0.08)"
      : "0 2px 12px rgba(60, 90, 130, 0.08)"};
  background: #fff;
  transition: box-shadow 0.2s, border 0.2s;
  overflow: hidden;
  &:hover {
    box-shadow: ${(props) =>
      props.$compact
        ? "0 2px 12px rgba(60, 90, 130, 0.14)"
        : "0 4px 24px rgba(60, 90, 130, 0.14)"};
  }
`;

const PaymentOptionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => (props.$compact ? "12px 14px" : "20px 24px")};
  cursor: pointer;
  background: ${(props) => (props.$selected ? "#eaf3ff" : "#fff")};
  border-left: ${(props) => (props.$compact ? "3px" : "5px")} solid
    ${(props) => (props.$selected ? "#2563eb" : "transparent")};
  transition: background 0.2s, border 0.2s;
`;

const RadioButton = styled.div`
  width: ${(props) => (props.$compact ? "18px" : "22px")};
  height: ${(props) => (props.$compact ? "18px" : "22px")};
  border-radius: 50%;
  border: 2px solid ${(props) => (props.$selected ? "#2563eb" : "#bfc9d9")};
  margin-right: ${(props) => (props.$compact ? "12px" : "18px")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  &::after {
    content: "";
    width: ${(props) => (props.$compact ? "9px" : "12px")};
    height: ${(props) => (props.$compact ? "9px" : "12px")};
    border-radius: 50%;
    background-color: ${(props) =>
      props.$selected ? "#2563eb" : "transparent"};
    transition: background 0.2s;
  }
`;

const PaymentLabel = styled.div`
  flex: 1;
  font-weight: ${(props) => (props.$selected ? "700" : "500")};
  font-size: ${(props) => (props.$compact ? "15px" : "17px")};
  display: flex;
  align-items: center;
`;

const GatewayBadge = styled.span`
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  padding: 3px 14px;
  margin-left: 14px;
  letter-spacing: 0.5px;
  display: inline-block;
  vertical-align: middle;
  box-shadow: 0 1px 4px rgba(37,99,235,0.08);
`;

const PaymentIcons = styled.div`
  display: flex;
  gap: ${(props) => (props.$compact ? "6px" : "10px")};
`;

const PaymentIcon = styled.img`
  height: ${(props) => (props.$compact ? "22px" : "28px")};
  width: auto;
`;

const PaymentDrawer = styled.div`
  padding: ${(props) => (props.$compact ? "12px 14px" : "18px 24px")};
  border-top: 1px solid #e3e8f0;
  background-color: #f7fafd;
`;

const DrawerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
`;

const RedirectMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RedirectIcon = styled.div`
  width: 60px;
  height: 60px;
  border: 1px solid #ddd;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  position: relative;

  &::after {
    content: "→";
    font-size: 24px;
    position: absolute;
  }
`;

const CurrencyWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff8f0;
  border: 1.5px solid #f5a623;
  border-radius: 14px;
  padding: 20px 22px;
  margin-top: 0px;
`;

const WarningIconCircle = styled.div`
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-top: 2px;
`;

const WarningTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WarningTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #92400e;
`;

const WarningDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #b45309;
  line-height: 1.6;
`;

const PaymentDeclineBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fef2f2;
  border: 1.5px solid #fca5a5;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const DeclineBannerText = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #991b1b;
  line-height: 1.5;
`;

// Map payment methods and platforms to their forms
const paymentMethodForms = {
  "card_paysafe": PaysafePaymentForm,
  "upi_dlocal": DlocalUpiForm,
  "pix_dlocal": DlocalPixForm,
  "card_dlocal": DlocalCardForm,
  "card_pagbrasil": PagbrasilCardForm,
  "pix_pagbrasil": PagbrasilPaymentForm,
  "card_checkoutcom": CheckoutComCardForm,
  "applepay_checkoutcom": CheckoutComApplePayForm,
  "kakaopay_checkoutcom": CheckoutComKakaopayForm,
  "openbanking_trustly": TrustlyOpenBankingForm,
  "card_adyen": AdyenCardForm,
  "card_nuvei": NuveiCardForm,
  [ALTRUON_VAULT_CARD_FORM_KEY]: BasisTheoryCardForm,
  // Add more mappings as needed
};

// Feature-gate methods that depend on device / browser capability.
// Returns false when the environment cannot support the method so we don't
// surface a dead tile (e.g. Apple Pay outside Safari / non-Apple devices).
const isMethodSupportedByDevice = (method) => {
  if (typeof window === "undefined") return false;
  if (method === "applepay") {
    try {
      return (
        !!window.ApplePaySession &&
        typeof window.ApplePaySession.canMakePayments === "function" &&
        window.ApplePaySession.canMakePayments()
      );
    } catch (_e) {
      return false;
    }
  }
  return true;
};

// Map payment methods to their labels - these will be replaced by translation function.
// We pass `defaultValue` so that when a key is missing the `translate()` helper returns
// the human-readable fallback instead of the literal dot-notation key.
const getPaymentMethodLabels = (t) => ({
  card: t('checkout.paymentMethods.labels.card', { defaultValue: 'Credit Card' }),
  upi: t('checkout.paymentMethods.labels.upi', { defaultValue: 'UPI' }),
  pix: t('checkout.paymentMethods.labels.pix', { defaultValue: 'Pix Automático' }),
  applepay: t('checkout.paymentMethods.labels.applepay', { defaultValue: 'Apple Pay' }),
  kakaopay: t('checkout.paymentMethods.labels.kakaopay', { defaultValue: 'KakaoPay' }),
  openbanking: t('checkout.paymentMethods.labels.openbanking', {
    defaultValue: 'Open Banking',
  }),
});

// Map payment methods and platforms to their icons
const paymentMethodIcons = {
  card: [visa, masterCard, amex, discover, jcb],
  applepay: [applepayLogo],
  upi: {
    dlocal: [], // Add UPI icon if needed
  },
  // pix: {   // enable PIX icon if needed
  //   dlocal: [dLocal], 
  //   pagbrasil: [pagbrasil],
  // },
};

const PaymentMethodSelector = ({
  selectedMethod,
  onChange,
  routingEntries,
  formData,
  itemDetails,
  billingPlatformId,
  planId,
  platformItemDetails,
  selectedAddons,
  checkoutPageConfDto,
  setCheckoutPageConfDto,
  isLoading,
  isRoutingLoaded = true,
  sessionId,
  compact = false,
  hideTitle = false,
  // Methods listed here keep their form mounted (so SDKs can init / render
  // their button into external slots) but hide the visual drawer chrome —
  // matches the Stripe-hosted-page style for Apple Pay where the row is just
  // a radio and the actual button lives in the CTA area.
  silentDrawerMethods = [],
}) => {
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(null);
  const [dlocalCardGatewayConnection, setDlocalCardGatewayConnection] = useState(null);
  const {
    setPaymentMethodName,
    setPlatformName,
    transactionStatus,
    citPaymentErrorMessage,
    hostedPaymentSelection,
    setHostedPaymentSelection,
  } = useCheckout();
  const hostedSelectionAppliedRef = useRef(false);

  const currentCurrency = itemDetails?.currencyCode;
  const uniqueOptions = useMemo(
    () =>
      dedupeRoutingOptions(
        filterRoutingEntriesByCurrency(routingEntries, currentCurrency).map(
          (entry) => ({
            method: entry.paymentMethod,
            platform: entry.platform,
            currency: entry.currency,
            id: entry.id,
            vaulting: entry.vaulting,
          })
        )
      ),
    [routingEntries, currentCurrency]
  );

  const dlocalCardRoutingId = useMemo(
    () =>
      uniqueOptions.find(
        (opt) => opt.method === "card" && opt.platform === "dlocal"
      )?.id ?? null,
    [uniqueOptions]
  );

  // Empty routing after load is a real "no methods" state — do not keep the
  // skeleton forever when the merchant deactivated every routing entry.
  const isPaymentMethodsLoading = isLoading || !isRoutingLoaded;
  const fetchDetails = async (id, planId) => {
    try {
      invalidateIdempotentGet(`/api/checkout/v1/${id}`);
      const data = await fetchCheckoutDetails(id, planId);
      if (data?.checkoutPageConfDto) {
        setCheckoutPageConfDto(data.checkoutPageConfDto);
      }
    } catch (error) {
      console.error("Something went wrong. Please try again later.");
    }
  };
  
  useEffect(() => {
  if (transactionStatus === "fail") {
      if (billingPlatformId && planId) {
        fetchDetails(billingPlatformId, planId);
      }
    
  }
}, [transactionStatus, billingPlatformId, planId]);

  // Prefetch dLocal card gateway connection once per routing id (not every render).
  // Uses shared fetchGatewayConnection cache so a simultaneously mounted card form
  // does not issue a second network request.
  useEffect(() => {
    if (!dlocalCardRoutingId) {
      setDlocalCardGatewayConnection(null);
      return;
    }

    let cancelled = false;
    devLog("🚀 Proactively fetching dLocal card gateway connection...");

    fetchGatewayConnection({ id: dlocalCardRoutingId })
      .then((data) => {
        if (cancelled) return;
        devLog("✅ dLocal card gateway connection prefetched:", data);
        setDlocalCardGatewayConnection(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(
          "❌ Failed to prefetch dLocal card gateway connection:",
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [dlocalCardRoutingId]);

  const handleMethodSelect = (method, platform) => {
    const formKey = `${method}_${platform}`.toLowerCase();
    onChange(method);
    setOpenDrawer(formKey);
    setPaymentMethodName(method);
    setPlatformName(platform);
  };

  useEffect(() => {
    if (!hostedPaymentSelection || hostedSelectionAppliedRef.current) return;
    if (!uniqueOptions.length) return;
    const { method, platform } = hostedPaymentSelection;
    const match = uniqueOptions.find(
      (opt) => opt.method === method && opt.platform === platform
    );
    if (match) {
      handleMethodSelect(match.method, match.platform);
      hostedSelectionAppliedRef.current = true;
      setHostedPaymentSelection(null);
    }
  }, [hostedPaymentSelection, uniqueOptions]);

  return (
    <> 
      {isPaymentMethodsLoading ? (
        <PaymentMethodsSkeleton compact={compact} showTitle={!hideTitle} />
      ) : (
          uniqueOptions.length === 0 ? (
            <PaymentMethodsContainer $compact={compact}>
              {!hideTitle && (
                <SectionTitle $compact={compact}>{t('checkout.paymentMethods.title')}</SectionTitle>
              )}
              <CurrencyWarningBanner>
                <WarningIconCircle>⚠️</WarningIconCircle>
                <WarningTextBlock>
                    <WarningTitle>
                      {t('checkout.paymentMethods.noMethodTitle')} {currentCurrency || 'this currency'}
                    </WarningTitle>
                    <WarningDescription>
                      {t('checkout.paymentMethods.noMethods', {
                        variables: { currency: currentCurrency || 'this currency' }
                      })}
                    </WarningDescription>
                </WarningTextBlock>
            </CurrencyWarningBanner>
            </PaymentMethodsContainer>
          ) : (
          <PaymentMethodsContainer $compact={compact}>
            {!hideTitle && (
              <SectionTitle $compact={compact}>{t('checkout.paymentMethods.title')}</SectionTitle>
            )}
            {citPaymentErrorMessage && (
              <PaymentDeclineBanner role="alert">
                <span aria-hidden="true">⚠️</span>
                <DeclineBannerText>{citPaymentErrorMessage}</DeclineBannerText>
              </PaymentDeclineBanner>
            )}
            {uniqueOptions.map((option, idx) => {
              const { method, platform } = option;
              const formKey = `${method}_${platform}`.toLowerCase();
              const FormComponent = resolvePaymentForm(option, paymentMethodForms);
              if (!FormComponent) return null;
              const isSelected = openDrawer === formKey;
              return (
                <PaymentOption key={formKey} $compact={compact}>
                  <PaymentOptionHeader
                    $selected={isSelected}
                    $compact={compact}
                    onClick={() => handleMethodSelect(method, platform)}
                  >
                    <RadioButton $selected={isSelected} $compact={compact} />
                    <PaymentLabel $selected={isSelected} $compact={compact}>
                      {getPaymentMethodLabels(t)[method] || method.toUpperCase()}
                      {/* <GatewayBadge>{platform}</GatewayBadge> */}
                    </PaymentLabel>
                    <PaymentIcons $compact={compact}>
                      {(paymentMethodIcons[method] && paymentMethodIcons[method][platform]
                        ? paymentMethodIcons[method][platform]
                        : paymentMethodIcons[method] || []
                      ).map((icon, i) => (
                        <PaymentIcon key={i} src={icon} alt={method} $compact={compact} />
                      ))}
                    </PaymentIcons>
                  </PaymentOptionHeader>
                  {isSelected && (() => {
                    const isSilent = silentDrawerMethods.includes(method);
                    const formNode = (
                      <FormComponent
                        formData={formData}
                        itemDetails={itemDetails}
                        billingPlatformId={billingPlatformId}
                        routingEntries={routingEntries}
                        planId={planId}
                        platformItemDetails={platformItemDetails}
                        selectedAddons={selectedAddons}
                        checkoutPageConfDto={checkoutPageConfDto}
                        prefetchedGatewayConnection={
                          formKey === 'card_dlocal' ? dlocalCardGatewayConnection : undefined
                        }
                        sessionId={sessionId}
                      />
                    );
                    if (isSilent) {
                      // Keep the form mounted so its lifecycle runs (e.g. Apple Pay
                      // button mounts into the external CTA slot), but hide the
                      // drawer chrome entirely so the row stays Stripe-style.
                      return (
                        <div style={{ display: 'none' }} aria-hidden="true">
                          {formNode}
                        </div>
                      );
                    }
                    return (
                      <PaymentDrawer $compact={compact}>
                        <DrawerContent>{formNode}</DrawerContent>
                      </PaymentDrawer>
                    );
                  })()}
                </PaymentOption>
              );
            })}
          </PaymentMethodsContainer>
        )
      )}
    </>
  );
};

export default PaymentMethodSelector;